import type { DifficultyId } from '../types/primitives'

/**
 * Difficulty definitions — ACTIVE for campaign stages (M49).
 * EnemyLevel remains a separate authored axis; difficulty scales effective stats.
 */
export interface DifficultyDefinition {
  id: DifficultyId
  displayName: string
  startingCoins: number
  /** Overall HP/shield-oriented pressure scalar (NOT applied to speed blindly). */
  enemyStatMultiplier: number
  killRewardMultiplier: number
  waveRewardMultiplier: number
}

export const DIFFICULTY_CATALOG: Record<DifficultyId, DifficultyDefinition> = {
  easy: {
    id: 'easy',
    displayName: 'Easy',
    startingCoins: 200,
    enemyStatMultiplier: 0.5,
    killRewardMultiplier: 0.75,
    waveRewardMultiplier: 0.8
  },
  normal: {
    id: 'normal',
    displayName: 'Normal',
    startingCoins: 300,
    enemyStatMultiplier: 1,
    killRewardMultiplier: 1,
    waveRewardMultiplier: 1
  },
  hard: {
    id: 'hard',
    displayName: 'Hard',
    startingCoins: 400,
    enemyStatMultiplier: 1.5,
    killRewardMultiplier: 1.35,
    waveRewardMultiplier: 1.25
  },
  insane: {
    id: 'insane',
    displayName: 'Insane',
    startingCoins: 500,
    enemyStatMultiplier: 2.5,
    killRewardMultiplier: 1.75,
    waveRewardMultiplier: 1.5
  }
}

export interface DifficultyStatPolicy {
  hp: number
  shield: number
  armorValue: number
  regen: number
  leakDamage: number
  /** Explicitly NOT equal to enemyStatMultiplier — Insane must not ×2.5 speed. */
  speed: number
  killReward: number
  waveReward: number
  startingCoins: number
}

export type DifficultyResolveMode = 'legacy' | 'provisional'

/**
 * Deterministic coin/reward rounding — floor after multiply.
 * No Math.random. Tie-break via tiny epsilon for float noise.
 */
export function scaleReward(base: number, multiplier: number): number {
  if (!Number.isFinite(base) || !Number.isFinite(multiplier)) return 0
  if (base <= 0) return 0
  return Math.floor(base * multiplier + 1e-9)
}

/**
 * Resolve how difficulty scales stats.
 * `legacy` = identity (Valley/Crossroads regression harnesses).
 * `provisional` = campaign difficulty (M49+). Normal remains identity for combat
 * stats but still uses catalog startingCoins when materializing stage maps.
 */
export function resolveDifficultyRules(
  difficultyId: DifficultyId,
  mode: DifficultyResolveMode = 'legacy'
): DifficultyStatPolicy {
  const def = DIFFICULTY_CATALOG[difficultyId]
  if (!def) throw new Error(`Unknown difficultyId: ${difficultyId}`)

  if (mode === 'legacy') {
    return {
      hp: 1,
      shield: 1,
      armorValue: 1,
      regen: 1,
      leakDamage: 1,
      speed: 1,
      killReward: 1,
      waveReward: 1,
      startingCoins: def.startingCoins
    }
  }

  // Normal = authored baseline (identity combat scaling).
  if (difficultyId === 'normal') {
    return {
      hp: 1,
      shield: 1,
      armorValue: 1,
      regen: 1,
      leakDamage: 1,
      speed: 1,
      killReward: 1,
      waveReward: 1,
      startingCoins: def.startingCoins
    }
  }

  const m = def.enemyStatMultiplier
  return {
    hp: m,
    shield: m,
    // Moderate armor curve — not full HP sponge.
    armorValue: 1 + (m - 1) * 0.4,
    // Careful regen curve.
    regen: 1 + (m - 1) * 0.5,
    // Leak capped so Insane does not one-shot the life pool per enemy.
    leakDamage: Math.min(m, 1.5),
    speed: 1,
    killReward: def.killRewardMultiplier,
    waveReward: def.waveRewardMultiplier,
    startingCoins: def.startingCoins
  }
}

export function difficultyPowerPercent(difficultyId: DifficultyId): number {
  return Math.round(DIFFICULTY_CATALOG[difficultyId].enemyStatMultiplier * 100)
}
